import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Filter, X, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { fetchCategories, fetchExpenses, createExpense, updateExpense, deleteExpense } from "../lib/api";
import { eur, formatDateIt, personColor } from "../lib/format";
import PeriodFilter from "../components/PeriodFilter";

const ALL = "__all__";
const SEARCH_DEBOUNCE_MS = 250;
const emptyForm = (date) => ({ date: date || new Date().toISOString().slice(0, 10), person: "Madda", category: "", subcategory: "", description: "", amount: "" });

export default function Expenses() {
  const [period, setPeriod] = useState({ year: undefined, month: undefined });
  const [person, setPerson] = useState(undefined);
  const [categoryFilter, setCategoryFilter] = useState(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // Debounce the search input
  useEffect(() => {
    const id = setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  const load = React.useCallback(async () => {
    const params = { ...period };
    if (person === "Famiglia") {
      params.family_only = true;
    } else if (person) {
      params.person = person;
    }
    if (categoryFilter) params.category = categoryFilter;
    if (searchQuery) params.q = searchQuery;
    const data = await fetchExpenses(params);
    setItems(data);
  }, [period, person, categoryFilter, searchQuery]);

  useEffect(() => {
    fetchCategories().then(setCategories);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedCategory = useMemo(() => categories.find((c) => c.name === form.category), [categories, form.category]);
  const categoryColorMap = useMemo(() => {
    const m = {};
    categories.forEach((c) => { if (c.color) m[c.name] = c.color; });
    return m;
  }, [categories]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      date: e.date,
      person: e.person,
      category: e.category,
      subcategory: e.subcategory || "",
      description: e.description || "",
      amount: String(e.amount),
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.date || !form.person || !form.category || form.amount === "" || form.amount === null || form.amount === undefined) {
      toast.error("Compila data, persona, categoria e importo");
      return;
    }
    let amount = parseFloat(form.amount);
    if (Number.isNaN(amount)) {
      toast.error("Importo non valido");
      return;
    }
    // Prelievo (withdrawal) → force negative amount
    if (form.category === "Prelievo") {
      amount = -Math.abs(amount);
    }
    const payload = {
      date: form.date,
      person: form.person,
      category: form.category,
      subcategory: form.subcategory || "",
      description: form.description || "",
      amount,
    };
    try {
      if (editing) {
        await updateExpense(editing.id, payload);
        toast.success(form.person === "Famiglia" ? "Spesa aggiornata e divisa 50/50" : "Spesa aggiornata");
      } else {
        await createExpense(payload);
        toast.success(form.person === "Famiglia" ? "Spesa aggiunta e divisa 50/50" : "Spesa aggiunta");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Eliminare la spesa "${e.description || e.category}" di ${eur(e.amount)}?`)) return;
    try {
      await deleteExpense(e.id);
      toast.success("Spesa eliminata");
      load();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const totalShown = useMemo(() => items.reduce((s, x) => s + x.amount, 0), [items]);

  return (
    <div className="space-y-8" data-testid="expenses-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Gestione</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold leading-tight">Spese</h1>
          <p className="text-stone-600 mt-2 max-w-xl">Aggiungi, modifica e filtra tutte le spese di Madda e Doio.</p>
        </div>
        <Button onClick={openCreate} className="pill-btn bg-[#377D22] hover:bg-[#2E5F1B] h-11 px-6 text-base" data-testid="add-expense-button">
          <Plus size={18} strokeWidth={2} className="mr-2" /> Aggiungi spesa
        </Button>
      </div>

      {/* Filters bar */}
      <div className="card-soft p-6 flex flex-wrap items-center gap-4" data-testid="expenses-filters">
        <div className="flex items-center gap-2 text-stone-500">
          <Filter size={16} strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-widest">Filtri</span>
        </div>
        <PeriodFilter idPrefix="exp" year={period.year} month={period.month} onChange={setPeriod} includePerson person={person} onPersonChange={setPerson} />
        <Select value={categoryFilter || ALL} onValueChange={(v) => setCategoryFilter(v === ALL ? undefined : v)}>
          <SelectTrigger className="w-[200px] rounded-xl bg-white border-stone-200" data-testid="exp-category-filter">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL} data-testid="exp-category-all">Tutte le categorie</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.name} data-testid={`exp-category-${c.name}`}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Cerca descrizione, categoria…"
            className="w-[260px] pl-9 rounded-xl bg-white border-stone-200"
            data-testid="exp-search-input"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-stone-100 text-stone-400"
              aria-label="Pulisci ricerca"
              data-testid="exp-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {(period.year || period.month || person || categoryFilter || searchInput) && (
          <button
            data-testid="clear-filters"
            onClick={() => { setPeriod({}); setPerson(undefined); setCategoryFilter(undefined); setSearchInput(""); }}
            className="pill-btn text-stone-500 hover:text-stone-900 text-sm flex items-center gap-1"
          >
            <X size={14} /> Reset
          </button>
        )}
        <div className="ml-auto text-sm text-stone-600">
          <span className="font-semibold tabular" data-testid="total-shown">{eur(totalShown)}</span>
          <span className="text-stone-400 ml-2">su {items.length} spese</span>
        </div>
      </div>

      {/* Table */}
      <div className="card-soft overflow-hidden" data-testid="expenses-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-stone-500 text-left">
                <th className="py-4 px-6">Data</th>
                <th className="py-4 px-6">Persona</th>
                <th className="py-4 px-6">Categoria</th>
                <th className="py-4 px-6">Sotto-categoria</th>
                <th className="py-4 px-6">Descrizione</th>
                <th className="py-4 px-6 text-right">Importo</th>
                <th className="py-4 px-6 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-stone-400" data-testid="expenses-empty">Nessuna spesa con i filtri attuali</td>
                </tr>
              )}
              {items.map((e) => (
                <tr key={e.id} className="border-t border-stone-100 hover:bg-stone-50/40" data-testid={`row-${e.id}`}>
                  <td className="py-4 px-6 tabular text-stone-600 whitespace-nowrap">{formatDateIt(e.date)}</td>
                  <td className="py-4 px-6">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: personColor(e.person) }} />
                      <span className="font-medium" style={{ color: personColor(e.person) }}>{e.person}</span>
                      {e.family_group_id && (
                        <span
                          className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#377D22] text-white"
                          title="Spesa Famiglia (50/50)"
                          data-testid={`fam-badge-${e.id}`}
                        >FAM</span>
                      )}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: categoryColorMap[e.category] || "#CBD5E1" }}
                      />
                      {e.category}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-stone-500">{e.subcategory || "—"}</td>
                  <td className="py-4 px-6 max-w-[300px] truncate">{e.description || "—"}</td>
                  <td className="py-4 px-6 text-right tabular font-semibold" style={{ color: personColor(e.person) }}>{eur(e.amount)}</td>
                  <td className="py-4 px-6 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(e)} className="pill-btn p-2 hover:bg-stone-100 mr-1" data-testid={`edit-${e.id}`} aria-label="Modifica">
                      <Pencil size={15} strokeWidth={1.5} />
                    </button>
                    <button onClick={() => remove(e)} className="pill-btn p-2 hover:bg-red-50 text-red-600" data-testid={`delete-${e.id}`} aria-label="Elimina">
                      <Trash2 size={15} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Modifica spesa" : "Nuova spesa"}</DialogTitle>
            <DialogDescription className="text-stone-500">
              {editing ? "Aggiorna i dettagli della spesa selezionata." : "Inserisci i dettagli della nuova spesa."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-1">
              <Label className="text-xs uppercase tracking-widest text-stone-500">Data</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl" data-testid="form-date" />
            </div>
            <div className="col-span-1">
              <Label className="text-xs uppercase tracking-widest text-stone-500">Persona</Label>
              <Select value={form.person} onValueChange={(v) => setForm({ ...form, person: v })}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="form-person"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Madda" data-testid="form-person-madda">Madda</SelectItem>
                  <SelectItem value="Doio" data-testid="form-person-doio">Doio</SelectItem>
                  <SelectItem value="Famiglia" data-testid="form-person-famiglia">Famiglia (50/50)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              <Label className="text-xs uppercase tracking-widest text-stone-500">Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, subcategory: "" })}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="form-category"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.name} data-testid={`form-category-${c.name}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              <Label className="text-xs uppercase tracking-widest text-stone-500">Sotto-categoria</Label>
              <Select value={form.subcategory} onValueChange={(v) => setForm({ ...form, subcategory: v })} disabled={!selectedCategory || selectedCategory.subcategories.length === 0}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="form-subcategory"><SelectValue placeholder={selectedCategory ? "Seleziona" : "Scegli categoria"} /></SelectTrigger>
                <SelectContent>
                  {(selectedCategory?.subcategories || []).map((s) => (
                    <SelectItem key={s} value={s} data-testid={`form-sub-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-stone-500">Descrizione</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-xl" data-testid="form-description" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-stone-500">Importo (€)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-xl tabular" data-testid="form-amount" />
              {form.category === "Prelievo" && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="hint-prelievo">
                  Categoria <span className="font-semibold">Prelievo</span>: l&apos;importo verrà registrato come negativo.
                </p>
              )}
              {form.person === "Famiglia" && (
                <p className="mt-2 text-xs text-stone-700 bg-stone-100 border border-stone-200 rounded-lg px-3 py-2" data-testid="hint-famiglia">
                  Spesa <span className="font-semibold">Famiglia</span>: verrà divisa 50/50 tra Madda e Doio (due movimenti separati).
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full" data-testid="form-cancel">Annulla</Button>
            <Button onClick={submit} className="rounded-full bg-[#377D22] hover:bg-[#2E5F1B]" data-testid="form-save">
              {editing ? "Salva modifiche" : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
